import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import {
  ArrowLeft,
  Trophy,
  Flame,
  Medal,
  Crown,
  Target
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_3e64acab-2561-42e6-b860-06b1a563df4d/artifacts/5nvw3yv2_6ECA6BBA-F429-4238-A743-EE6BF2D1D33F.png";

export default function Leaderboard() {
  const { user } = useAuth();
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchLeaderboard();
  }, []);

  const fetchLeaderboard = async () => {
    try {
      const response = await axios.get(`${API}/leaderboard`);
      setLeaderboard(response.data.leaderboard || []);
    } catch (error) {
      console.error("Failed to fetch leaderboard:", error);
    } finally {
      setLoading(false);
    }
  };

  const getRankIcon = (rank) => {
    switch (rank) {
      case 1:
        return <Crown className="h-6 w-6 text-yellow-500" />;
      case 2:
        return <Medal className="h-6 w-6 text-gray-400" />;
      case 3:
        return <Medal className="h-6 w-6 text-amber-600" />;
      default:
        return <span className="text-lg font-mono font-bold text-muted-foreground">{rank}</span>;
    }
  };

  const getRankStyle = (rank) => {
    switch (rank) {
      case 1:
        return "border-yellow-500/50 bg-yellow-500/10";
      case 2:
        return "border-gray-400/50 bg-gray-400/10";
      case 3:
        return "border-amber-600/50 bg-amber-600/10";
      default:
        return "border-border bg-card";
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-primary animate-pulse text-2xl font-bold">LOADING...</div>
      </div>
    );
  }

  const userRank = leaderboard.find(l => l.user_id === user?.id);

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-40 bg-background/80 backdrop-blur-lg border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/dashboard" className="flex items-center gap-2 text-muted-foreground hover:text-foreground">
              <ArrowLeft className="h-5 w-5" /> Back
            </Link>
            <div className="flex items-center gap-2">
              <img src={LOGO_URL} alt="Coach Coop" className="h-8 w-8 rounded-sm" />
              <span className="font-bold tracking-tight">LEADERBOARD</span>
            </div>
            <div className="w-16" />
          </div>
        </div>
      </header>

      <main className="pt-24 pb-12 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto">
        {/* Your Rank */}
        {userRank && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8"
          >
            <Card className="bg-primary/10 border-primary/30">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center">
                      {getRankIcon(userRank.rank)}
                    </div>
                    <div>
                      <div className="text-sm text-primary uppercase tracking-wider">Your Rank</div>
                      <div className="text-2xl font-black">#{userRank.rank}</div>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-6 text-center">
                    <div>
                      <div className="text-2xl font-black font-mono text-orange-500">{userRank.streak}</div>
                      <div className="text-xs text-muted-foreground uppercase">Streak</div>
                    </div>
                    <div>
                      <div className="text-2xl font-black font-mono text-accent">{userRank.current_day}</div>
                      <div className="text-xs text-muted-foreground uppercase">Day</div>
                    </div>
                    <div>
                      <div className="text-2xl font-black font-mono text-primary">{userRank.completion_percentage}%</div>
                      <div className="text-xs text-muted-foreground uppercase">Complete</div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Leaderboard Header */}
        <Card className="bg-card border-border mb-6">
          <CardHeader>
            <CardTitle className="uppercase tracking-tighter flex items-center gap-2">
              <Trophy className="h-5 w-5 text-primary" />
              Top Performers
            </CardTitle>
          </CardHeader>
        </Card>

        {/* Leaderboard List */}
        {leaderboard.length > 0 ? (
          <div className="space-y-3">
            {leaderboard.map((entry, i) => (
              <motion.div
                key={entry.user_id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.05 }}
              >
                <Card className={`border ${getRankStyle(entry.rank)} ${entry.user_id === user?.id ? 'ring-2 ring-primary' : ''}`}>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-4">
                      {/* Rank */}
                      <div className="w-10 h-10 flex items-center justify-center flex-shrink-0">
                        {getRankIcon(entry.rank)}
                      </div>
                      
                      {/* Name */}
                      <div className="flex-1 min-w-0">
                        <div className="font-bold uppercase tracking-tight truncate">
                          {entry.name}
                          {entry.user_id === user?.id && (
                            <span className="ml-2 text-xs text-primary">(You)</span>
                          )}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          Day {entry.current_day} of 60
                        </div>
                      </div>
                      
                      {/* Stats */}
                      <div className="flex items-center gap-4 flex-shrink-0">
                        <div className="text-center">
                          <div className="flex items-center gap-1 justify-center">
                            <Flame className="h-4 w-4 text-orange-500" />
                            <span className="font-black font-mono text-orange-500">{entry.streak}</span>
                          </div>
                          <div className="text-xs text-muted-foreground">streak</div>
                        </div>
                        <div className="text-center">
                          <div className="flex items-center gap-1 justify-center">
                            <Target className="h-4 w-4 text-accent" />
                            <span className="font-black font-mono text-accent">{entry.completion_percentage}%</span>
                          </div>
                          <div className="text-xs text-muted-foreground">complete</div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        ) : (
          <Card className="bg-card border-border">
            <CardContent className="p-12 text-center">
              <Trophy className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-xl font-bold uppercase tracking-tighter mb-2">No Rankings Yet</h3>
              <p className="text-muted-foreground">
                Start your subscription to appear on the leaderboard.
              </p>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
}
